import { Component, Injector, Signal, WritableSignal, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { ColDef, EditableCallbackParams } from 'ag-grid-community'; // Column Definition Type Interface

import { SessionService } from '../../services/session.service';
import { TableService } from '../../services/table.service';

import { authorizedMarshal } from '../../auth/auth.guard';

import { Campaign } from '../../interfaces/campaign';

interface tableMetaData {
  title: string,
  colDefs: ColDef[],
  tableType: string,
  data?: WritableSignal<any[]>;
};

const StarTypeWithNoPlanets = ['Blue Giant', 'White Dwarf', 'Red Giant'];

@Component({
  selector: 'campaign-tables',
  templateUrl: './campaign-tables.component.html',
  standalone: false
})
export class CampaignTablesComponent {
  session = inject(SessionService);
  tableService = inject(TableService);

  dirty: WritableSignal<boolean> = signal(false);

  defaultColDef: ColDef = {
    editable: true,
    onCellValueChanged: (event: any) => {
      this.dirty.set(true);
    }
  };

  tables: tableMetaData[] = [
    {
      title: "System Types",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'type', width: 150, minWidth: 150 },
        { field: 'wpModifier', width: 140, minWidth: 140, type: 'rightAligned', headerName: 'WP Modifier', headerTooltip: 'Warp Point Modifier' },
      ],
      tableType: 'systemTypes'
    },
    {
      title: "Star Types",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'type', headerName: 'Type', width: 140, minWidth: 140 },
        { field: 'diameter', headerName: 'Diameter', headerTooltip: 'Diameter', width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'wpModifier', headerName: 'WPMod', headerTooltip: 'Warp Point Modifier', width: 80, minWidth: 80, type: 'rightAligned' },
        {
          field: 'rockyMax', headerName: 'Rocky Max', headerTooltip: 'Rocky Max', width: 80, minWidth: 80, type: 'rightAligned',
          // conditionally enables editing
          editable: (params) => this.isCellEditable(params)
        },
        {
          field: 'gasMin', headerName: 'GMin', headerTooltip: 'Gas Minimum', width: 80, minWidth: 80, type: 'rightAligned',
          editable: (params) => this.isCellEditable(params)
        },
        {
          field: 'gasMax', headerName: 'GMax', headerTooltip: 'Gas Maximum', width: 80, minWidth: 80, type: 'rightAligned',
          editable: (params) => this.isCellEditable(params)
        },
        {
          field: 'iceMin', headerName: 'IMin', headerTooltip: 'Ice Minimum', width: 80, minWidth: 80, type: 'rightAligned',
          editable: (params) => this.isCellEditable(params)
        },
        {
          field: 'biosphereMin', headerName: 'BioMin', headerTooltip: 'Biosphere Minimum', width: 80, minWidth: 80, type: 'rightAligned',
          editable: (params) => this.isCellEditable(params)
        },
        {
          field: 'biosphereMax', headerName: 'BioMax', headerTooltip: 'Biosphere Maximum', width: 80, minWidth: 80, type: 'rightAligned',
          editable: (params) => this.isCellEditable(params)
        },
        {
          field: 'tideLockMax', headerName: 'TMax', headerTooltip: 'Tidelock Maximum', width: 80, minWidth: 80, type: 'rightAligned',
          editable: (params) => this.isCellEditable(params)
        },
        {
          field: 'maxOrbit', headerName: 'Max Orbit', headerTooltip: 'Max Orbit', width: 80, minWidth: 80, type: 'rightAligned',
          editable: (params) => this.isCellEditable(params)
        },
        // { field: 'info', headerName: 'Info', headerTooltip: 'Info', width: 80, minWidth: 80 },
      ],
      tableType: 'starTypes'
    },
    {
      title: "Binary Distances",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'distance', width: 100, minWidth: 100, type: 'rightAligned' },
        // { field: 'info', flex: 1 },
      ],
      tableType: 'binaryDistances'
    },
    {
      title: "Rocky Planet Types",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'innerType', width: 140, minWidth: 140 },
        { field: 'biosphereType', width: 160, minWidth: 160 },
        { field: 'outerType', width: 140, minWidth: 140 },
        // { field: 'info', flex: 1 },
      ],
      tableType: 'rockyPlanetTypes'
    },
    {
      title: "Planet Moon Count Modifiers",
      colDefs: [
        { field: 'type', width: 80, minWidth: 80 },
        { field: 'modifier', width: 100, minWidth: 100, type: 'rightAligned' },
      ],
      tableType: 'planetMoonCountModifiers'
    },
    {
      title: "Moon Counts",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'count', width: 80, minWidth: 80, type: 'rightAligned' },
        // { field: 'info', flex: 1 },
      ],
      tableType: 'moonCounts'
    },
    {
      title: "Warp Point Numbers",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'count', width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'dieRoll', width: 100, minWidth: 100, type: 'rightAligned' },
        // { field: 'info', flex: 1 },
      ],
      tableType: 'warpPointNumbers'
    },
    {
      title: "Warp Point Types",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'type', width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'open', width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'capacity', width: 110, minWidth: 110, type: 'rightAligned' },
        // { field: 'info', flex: 1 },
      ],
      tableType: 'warpPointTypes'
    },
    {
      title: "Warp Point Distances",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'hexDistance', headerName: 'Hex Distances []', flex: 2 }
      ],
      tableType: 'warpPointDistances'
    },
    {
      title: "Civilization Probabilities",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'dieModifier', headerName: 'Modifier', width: 130, minWidth: 130, type: 'rightAligned' },
        { field: 'type', width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'typeName', flex: 1 },
        // { field: 'info', flex: 1 },
      ],
      tableType: 'civilizationProbabilities'
    },
    {
      title: "Population Sizes",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'multiplier', width: 110, minWidth: 110, type: 'rightAligned' },
        { field: 'addition', width: 100, minWidth: 100, type: 'rightAligned' },
        { field: 'info', flex: 1 },
      ],
      tableType: 'populationSizes'
    },
    {
      title: "Governments",
      colDefs: [
        { field: 'governmentId', headerName: 'ID', editable: false, width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'statLookup', headerName: 'STAT', editable: false, width: 80, minWidth: 80 },
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'name', headerName: 'Name', width: 260, minWidth: 260 },
        { field: 'contact', headerName: 'Contact', headerTooltip: 'Contact', width: 100, minWidth: 100, type: 'rightAligned' },
        { field: 'trade', headerName: 'Trade', headerTooltip: 'Trade', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'military', headerName: 'Mil', headerTooltip: 'Military', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'partnership', headerName: 'Part', headerTooltip: 'Partnership', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'amalgation', headerName: 'Amal', headerTooltip: 'Amalgamation', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'warships', headerName: 'Warships', headerTooltip: 'Warships', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'defense', headerName: 'Defense', headerTooltip: 'Defense', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'industrial', headerName: 'IU', headerTooltip: 'Industrial', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'freighters', headerName: 'Freighters', headerTooltip: 'Freighters', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'pcf', headerName: 'PCF', headerTooltip: 'PCF', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'grade', headerName: 'Grade', headerTooltip: 'Grade', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'shipyard', headerName: 'SY', headerTooltip: 'Shipyard Adjustment', width: 70, minWidth: 70, type: 'rightAligned' }
      ],
      tableType: 'governments'
    },
    {
      title: "System Exploitations",
      colDefs: [
        { field: 'low', headerName: 'Low', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'high', headerName: 'High', width: 70, minWidth: 70, type: 'rightAligned' },
        { field: 'ptu', width: 80, minWidth: 80, type: 'rightAligned' },
        // { field: 'info', flex: 1 },
      ],
      tableType: 'systemExploitations'
    },
    {
      title: "Economic Level Research",
      colDefs: [
        { field: 'code', headerName: 'Code', editable: false, width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'startUpFee', width: 140, minWidth: 140, type: 'rightAligned' },
        { field: 'monthlyFee', width: 140, minWidth: 140, type: 'rightAligned' },
        { field: 'requiredPoints', width: 160, minWidth: 160, type: 'rightAligned' },
        { field: 'description', flex: 1 },
      ],
      tableType: 'economicItems'
    },
    {
      title: "To Hit",
      colDefs: [
        { field: 'name', headerName: 'Name', editable: false, width: 120, minWidth: 120 },
        { field: 'toHit', headerName: 'To Hit Array', editable: true, flex: 1 },
      ],
      tableType: 'toHit'
    },
    {
      title: "Damage",
      colDefs: [
        { field: 'name', headerName: 'Name', editable: false, width: 120, minWidth: 120 },
        { field: 'damage', headerName: 'Damage Array', editable: true, flex: 1 },
      ],
      tableType: 'damage'
    },
    {
      title: "Project Items",
      colDefs: [
        { field: 'techLevel', headerName: 'Tech Level', headerTooltip: 'Tech Level', width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'code', headerName: 'Code', editable: false, width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'cost', headerName: 'Cost', width: 80, minWidth: 80, type: 'rightAligned' },
        { field: 'developmentCost', headerName: 'Dev Cost', width: 120, minWidth: 120, type: 'rightAligned' },
        { field: 'developmentPoints', headerName: 'Dev Points', width: 120, minWidth: 120, type: 'rightAligned' },
        { field: 'hullSpace', headerName: 'Hull Space', width: 120, minWidth: 120, type: 'rightAligned' },
        { field: 'description', headerName: 'Description', width: 240, minWidth: 240 },
        { field: 'symbol', headerName: 'Symbol', width: 140, minWidth: 140 },
        { field: 'notations', headerName: 'Notations', headerTooltip: 'Notations', width: 190, minWidth: 190 }
      ],
      tableType: 'projectItems'
    }
  ];

  authorizedMarshal: boolean;
  pageName: string = "campaign-tables";
  campaign: Campaign;
  selectedTable: WritableSignal<tableMetaData> = signal(undefined);
  data: WritableSignal<any[]> = signal([]);
  originalData: any[];
  columnDefs: ColDef[] = [];
  customized: WritableSignal<boolean> = signal(false);

  constructor () {
    this.authorizedMarshal = authorizedMarshal();
    this.campaign = this.session.getCampaign();
    this.loadAllTableData();

    effect(() => {
      let data = this.selectedTable()?.data() || [];
      if (!this.selectedTable() || data.length === 0) {
        return;
      }

      this.originalData = data.map(
        item => { return Object.assign({}, item); }
      );
      this.data.set(data.map(
        item => { return Object.assign({}, item); })
      );
      this.dirty.set(false);
      this.customized.set(this.originalData[0]?.custom || false);
    });
  };

  ngOnInit (): void {
  };

  refreshData () {
    for (let table of this.tables) {
      this.tableService.clearTableDataCacheForType(table.tableType, this.campaign._id);
      table.data = signal([]);
      this.loadTableData(table);
    }
    setTimeout(() => {
      this.displayTable(this.tables[0]);
    });
  };

  isCellEditable (params) {
    let canEdit = !StarTypeWithNoPlanets.includes(params.data.type);
    return canEdit;
  };

  loadTableData (table: any) {
    console.log('loadTableData', table.tableType);
    this.tableService.getTableDataForType(table.tableType, this.campaign._id).subscribe(
      data => {
        table.data.set(data);
      }
    );
  };

  loadAllTableData () {
    for (let table of this.tables) {
      table.data = signal([]);
      this.loadTableData(table);
    }
    setTimeout(() => {
      this.displayTable(this.tables[0]);
    });
  };

  displayTable (table: tableMetaData) {
    // actual table display data is set via the effect in the constructor
    this.selectedTable.set(table);
    this.columnDefs = table['colDefs'];
  };

  saveTableData (table: tableMetaData, changedData: WritableSignal<any[]>) {
    setTimeout(() => {
      table.data.set([]);
      this.originalData = changedData().map(item => { return Object.assign({}, item); });
      this.tableService.saveTableData(table, changedData(), this.campaign._id).subscribe(
        data => {
          table.data.set(data);
          this.displayTable(table);
        }
      );
      this.session.setNotifyMessage('Table ' + table.title + ' data has been saved.');
    });
  };

  deleteTableData (table: tableMetaData, campaignId: string) {
    this.tableService.deleteTableDataForType(table.tableType, campaignId).subscribe(
      data => {
        this.tableService.processTable(campaignId, table.tableType, data);
        table.data.set(data);
        this.displayTable(table);
        this.session.setNotifyMessage('Table ' + table.title + ' data has been deleted, now using default values.');
      }
    );
  };

  revertChanges (table: tableMetaData) {
    table.data.set(this.originalData);
    this.data.set(this.originalData);
    this.session.setNotifyMessage('Table ' + table.title + ' data has been reverted.');
  };
}
